import * as React from 'react';
import { cn } from '@/utils/cn';
import type { Message } from '@/types';
import { ChatMessage } from './ChatMessage';

export interface EchoInfoEntry {
  id: string;
  name: string;
  avatarUrl?: string;
}

export interface MessageListProps {
  messages: Message[];
  isLoading?: boolean;
  className?: string;
  echoName?: string;
  isTransitioning?: boolean;
  echoId?: string;
  isAudioEnabled?: boolean;
  /** Map of echo_id → display info for group chat messages. */
  echoInfoMap?: Record<string, EchoInfoEntry>;
  /** Hide action buttons (play, copy, rate) under AI messages. */
  hideActions?: boolean;
  /** Whether the echo supports per-message video generation. */
  videoMessagesEnabled?: boolean;
}

export const MessageList = React.forwardRef<HTMLDivElement, MessageListProps>(
  (
    {
      messages,
      className,
      echoName,
      isTransitioning,
      echoId,
      isAudioEnabled,
      echoInfoMap,
      hideActions,
      videoMessagesEnabled,
    },
    forwardedRef
  ) => {
    const bottomRef = React.useRef<HTMLDivElement>(null);
    const previousLastMessageIdRef = React.useRef<string | null>(null);
    const lastMessage = messages[messages.length - 1];
    const lastMessageId = lastMessage?.clientMessageId ?? lastMessage?.id ?? null;
    const lastMessageContent = lastMessage?.content ?? '';
    const lastMessageIsTextComplete = lastMessage?.isTextComplete ?? false;

    // Scroll the nearest overflow-y-auto ancestor (the chat scroll container),
    // not the page — `scrollIntoView`'s default would scroll the whole document.
    // Audio-only message updates must not trigger scrolling.
    React.useEffect(() => {
      if (!bottomRef.current || !lastMessageId) return;

      const shouldSmoothScroll =
        previousLastMessageIdRef.current !== null &&
        previousLastMessageIdRef.current !== lastMessageId;
      const behavior: ScrollBehavior = shouldSmoothScroll ? 'smooth' : 'auto';
      const scrollContainer = bottomRef.current.closest('.overflow-y-auto') as HTMLElement | null;
      if (scrollContainer) {
        scrollContainer.scrollTo({ top: scrollContainer.scrollHeight, behavior });
      } else {
        bottomRef.current.scrollIntoView({ behavior, block: 'nearest' });
      }

      previousLastMessageIdRef.current = lastMessageId;
    }, [lastMessageContent, lastMessageId, lastMessageIsTextComplete]);

    if (messages.length === 0) {
      return (
        <div className='flex min-h-[200px] flex-col items-center justify-center p-8 text-center'>
          <div className='text-gray-700'>
            <p className='font-proxima text-2xl font-bold'>No messages yet</p>
            <p className='mt-2 text-base text-gray-500'>Start chatting below!</p>
          </div>
        </div>
      );
    }

    return (
      <div ref={forwardedRef} className={cn('space-y-4', className)}>
        {messages.map((message) => {
          const msgEchoId = (message as Message & { echo_id?: string | null }).echo_id;
          const echoInfo = msgEchoId && echoInfoMap ? echoInfoMap[msgEchoId] : undefined;
          return (
            <ChatMessage
              key={message.clientMessageId ?? message.id}
              message={message}
              {...(echoName !== undefined ? { echoName } : {})}
              {...((echoInfo?.id ?? echoId) !== undefined
                ? { echoId: echoInfo?.id ?? echoId }
                : {})}
              isTransitioning={!!isTransitioning}
              {...(isAudioEnabled !== undefined ? { isAudioEnabled } : {})}
              {...(echoInfo !== undefined ? { echoInfo } : {})}
              {...(hideActions !== undefined ? { hideActions } : {})}
              {...(videoMessagesEnabled !== undefined ? { videoMessagesEnabled } : {})}
            />
          );
        })}
        {/* Target for scroll calculations */}
        <div ref={bottomRef} />
      </div>
    );
  }
);

MessageList.displayName = 'MessageList';
